import * as React from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";

import {
  Box,
  Card,
  CardContent,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import { exportTableToPDF } from "./ExportTablePDF";

export default function Table({
  rows = [],
  columns = [],

  title = "Report",
  showTitle = true,

  isDark = false,

  searchable = false,
  search = "",
  onSearchChange,
  searchPlaceholder = "Search...",

  exportable = false,
  exportTitle,
  exportFileName,

  getRowId = (row) => row.id,

  height = 600,
  pageSize = 50,
  rowsPerPageOptions = [25, 50, 100],
  showToolbar = true,

  className = "",
}) {
  /*
   * ------------------------------------------------------------
   * Internal search state
   *
   * If the parent supplies search/onSearchChange,
   * those are used instead.
   *
   * Otherwise the table manages its own search.
   * ------------------------------------------------------------
   */
  const [internalSearch, setInternalSearch] =
    React.useState("");

  const currentSearch =
    onSearchChange !== undefined
      ? search
      : internalSearch;

  const handleSearchChange = (value) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearch(value);
    }
  };

  /*
   * ------------------------------------------------------------
   * Filter rows
   *
   * Search across all visible column values.
   * ------------------------------------------------------------
   */
  const filteredRows = React.useMemo(() => {
    if (!searchable || !currentSearch.trim()) {
      return rows;
    }

    const searchValue =
      currentSearch.trim().toLowerCase();

    return rows.filter((row) =>
      columns.some((column) => {
        const value = row?.[column.field];

        if (
          value === null ||
          value === undefined
        ) {
          return false;
        }

        return String(value)
          .toLowerCase()
          .includes(searchValue);
      })
    );
  }, [
    rows,
    columns,
    searchable,
    currentSearch,
  ]);

  /*
   * ------------------------------------------------------------
   * PDF Export
   * ------------------------------------------------------------
   */
  const handleExportPDF = () => {
    const defaultName =
      exportFileName ||
      `${exportTitle || title || "Report"} Report`;

    const fileName = window.prompt(
      "Enter a name for your PDF report:",
      defaultName
    );

    if (fileName === null) {
      return;
    }

    const trimmedName = fileName.trim();

    if (!trimmedName) {
      return;
    }

    exportTableToPDF({
      rows: filteredRows,
      columns,
      title:
        exportTitle ||
        title ||
        "Report",
      fileName: trimmedName,
    });
  };

  return (
    <Box
      className={`${className} min-w-0`}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Card
        variant="outlined"
        className="rounded-lg shadow-sm"
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",

          backgroundColor: isDark
            ? "#111827"
            : "#ffffff",

          borderColor: isDark
            ? "#374151"
            : "#e5e7eb",
        }}
      >
        {/* HEADER */}
        {(showTitle ||
          searchable ||
          exportable) && (
          <CardContent className="!p-4 !pb-4">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 min-w-0">

              {/* TITLE */}
              {showTitle && (
                <Typography
                  component="h3"
                  className="text-blue-700 dark:text-blue-300 font-semibold text-lg"
                  sx={{
                    minWidth: 0,
                    flexShrink: 1,
                  }}
                >
                  {title}
                </Typography>
              )}

              {/* SEARCH + EXPORT */}
              <div className="flex items-center gap-[15px] min-w-0">

                {/* SEARCH */}
                {searchable && (
                  <TextField
                    size="small"
                    placeholder={
                      searchPlaceholder
                    }
                    value={currentSearch}
                    onChange={(event) =>
                      handleSearchChange(
                        event.target.value
                      )
                    }
                    sx={{
                      width: {
                        xs: "100%",
                        sm: 260,
                      },

                      flexShrink: 1,

                      "& .MuiInputBase-root":
                        {
                          backgroundColor:
                            isDark
                              ? "#1f2937"
                              : "#ffffff",

                          color: isDark
                            ? "#f3f4f6"
                            : "#111827",
                        },

                      "& .MuiInputBase-input::placeholder":
                        {
                          color: isDark
                            ? "#9ca3af"
                            : "#6b7280",

                          opacity: 1,
                        },

                      "& .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: isDark
                            ? "#4b5563"
                            : "#d1d5db",
                        },

                      "&:hover .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: isDark
                            ? "#6b7280"
                            : "#9ca3af",
                        },

                      "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "#1976d2",
                        },
                    }}
                  />
                )}

                {/* EXPORT PDF */}
                {exportable && (
                  <Tooltip title="Export PDF">
                    <IconButton
                      onClick={
                        handleExportPDF
                      }
                      aria-label="Export PDF"
                      sx={{
                        flexShrink: 0,

                        color: isDark
                          ? "#90caf9"
                          : "#1976d2",
                      }}
                    >
                      <PictureAsPdfIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            </div>
          </CardContent>
        )}

        {/* DATA GRID */}
        <Box
          sx={{
            height,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}

            initialState={{
              pagination: {
                paginationModel: {
                  pageSize,
                  page: 0,
                },
              },
            }}

            pageSizeOptions={
              rowsPerPageOptions
            }

            disableRowSelectionOnClick

            slots={
              showToolbar
                ? {
                    toolbar: GridToolbar,
                  }
                : undefined
            }

            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,

              boxSizing: "border-box",

              /* Main grid */
              backgroundColor: isDark
                ? "#1f2937"
                : "#ffffff",

              color: isDark
                ? "#e5e7eb"
                : "#111827",

              borderColor: isDark
                ? "#374151"
                : "#e5e7eb",

              /* Prevent grid overflow */
              "& .MuiDataGrid-main": {
                minWidth: 0,
                maxWidth: "100%",
              },

              /* Column headers */
              "& .MuiDataGrid-columnHeaders":
                {
                  backgroundColor: isDark
                    ? "#374151"
                    : "#f4f4f4",

                  fontWeight: "bold",

                  color: isDark
                    ? "#f3f4f6"
                    : "#111827",

                  borderBottomColor: isDark
                    ? "#4b5563"
                    : "#e5e7eb",
                },

              "& .MuiDataGrid-columnHeader":
                {
                  backgroundColor: isDark
                    ? "#374151"
                    : "#f4f4f4",
                },

              "& .MuiDataGrid-columnHeaderTitle":
                {
                  color: isDark
                    ? "#f9fafb"
                    : "#111827",

                  fontWeight: 700,
                },

              /* Header separator */
              "& .MuiDataGrid-iconSeparator":
                {
                  color: isDark
                    ? "#6b7280"
                    : "#d1d5db",
                },

              /* Cells */
              "& .MuiDataGrid-cell": {
                borderBottomColor: isDark
                  ? "#374151"
                  : "#e5e7eb",

                color: isDark
                  ? "#e5e7eb"
                  : "#111827",
              },

              /* Hover */
              "& .MuiDataGrid-row:hover": {
                backgroundColor: isDark
                  ? "#111827"
                  : "#f9fafb",
              },

              /* Selected */
              "& .MuiDataGrid-row.Mui-selected":
                {
                  backgroundColor: isDark
                    ? "#334155"
                    : "#DBEAFE",
                },

              "& .MuiDataGrid-row.Mui-selected:hover":
                {
                  backgroundColor: isDark
                    ? "#3b4a62"
                    : "#BFDBFE",
                },

              /* Footer */
              "& .MuiDataGrid-footerContainer":
                {
                  borderTopColor: isDark
                    ? "#374151"
                    : "#e5e7eb",

                  backgroundColor: isDark
                    ? "#1f2937"
                    : "#ffffff",
                },

              /* Pagination */
              "& .MuiTablePagination-root":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              "& .MuiTablePagination-selectLabel":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              "& .MuiTablePagination-displayedRows":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              /* Icons */
              "& .MuiSvgIcon-root": {
                color: isDark
                  ? "#d1d5db"
                  : "#374151",
              },

              /* Toolbar */
              "& .MuiDataGrid-toolbarContainer":
                {
                  backgroundColor: isDark
                    ? "#1f2937"
                    : "#ffffff",
                },

              "& .MuiDataGrid-toolbarContainer button":
                {
                  color: isDark
                    ? "#d1d5db"
                    : "#374151",
                },

              /* Toolbar inputs */
              "& .MuiDataGrid-toolbarContainer .MuiInputBase-root":
                {
                  color: isDark
                    ? "#e5e7eb"
                    : "#111827",
                },

              "& .MuiDataGrid-toolbarContainer .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: isDark
                    ? "#4b5563"
                    : "#d1d5db",
                },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
}